// src/App.tsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import Sidebar from './components/Sidebar';
import CompanySelector from './components/CompanySelector';
import PrivateRoute from './components/PrivateRoute';
import GeneralLedgerPage from './pages/GeneralLedgerPage';
import BalanceSheetPage from './pages/BalanceSheetPage';
import ProfitLossPage from './pages/ProfitLossPage';
import SalesReportPage from "./pages/SalesReportPage";
import DashboardPage from './pages/DashboardPage';
import LoginForm from './components/LoginForm';
import OutstandingReceivablePage from './pages/OutstandingReceivable';
import PurchaseReportPage from './pages/PurchaseReportPage';
import { useAuthStore } from './context/AuthContext';

import axios from 'axios';
import './App.css';

axios.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token; // Get token from Zustand store
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Axios interceptor for handling 401 Unauthorized responses
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // If 401, log out the user and redirect to login
      useAuthStore.getState().logout();
      // Use window.location.href to force a full page reload and clear state
      // This is often simpler for auth errors than navigate() which might not clear all state immediately.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Layout for authenticated routes
const AuthenticatedLayout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState) {
      setSidebarCollapsed(savedState === 'true');
    }
  }, []);

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };

  return (
    <div className="app-container">
      <Sidebar collapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />

      <div className="main-content">
        <header className="bg-white shadow p-4 flex justify-between items-center">
          <h1 className="text-2xl font-semibold">Financial Reports</h1>
          <div className="flex items-center">
            <CompanySelector />
            <button
              onClick={() => useAuthStore.getState().logout()}
              className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 ml-4"
            >
              Logout
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile toggle button (only visible on small screens when sidebar is collapsed) */}
      {sidebarCollapsed && (
        <button
          className="mobile-toggle-btn"
          onClick={toggleSidebar}
          aria-label="Open sidebar"
        >
          <Bars3Icon className="h-6 w-6 text-gray-500" />
        </button>
      )}
    </div>
  );
};

const AppContent: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const _hasHydrated = useAuthStore((state) => state._hasHydrated);
  const navigate = useNavigate();

  // Redirect to login if not authenticated on initial load
  useEffect(() => {
    if (!_hasHydrated) {
      return; // Wait until hydration is complete
    }

    console.log('Authenticated', isAuthenticated);

    // Scenario 1: Not authenticated AND not on the login page -> Redirect to login
    if (!isAuthenticated && window.location.pathname !== '/login') {
      navigate('/login', { replace: true });
    }
    // Scenario 2: Authenticated AND currently on the login page -> Redirect to dashboard
    if (isAuthenticated && window.location.pathname === '/login') {
      navigate('/', { replace: true });
    }
  }, [_hasHydrated, isAuthenticated, navigate]); // Depend on hydration and auth status

  if (!_hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-xl text-gray-700">Loading application...</p>
        {/* You could add a proper loading spinner here */}
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginForm />} />

      {/* Protected routes use AuthenticatedLayout */}
      <Route element={<PrivateRoute />}>
        <Route element={<AuthenticatedLayout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/general-ledger" element={<GeneralLedgerPage />} />
          <Route path="/balance-sheet" element={<BalanceSheetPage />} />
          <Route path="/profit-loss" element={<ProfitLossPage />} />
          <Route path="/sales-report" element={<SalesReportPage />} />
          <Route path="/outstanding-receivable" element={<OutstandingReceivablePage />} />
          <Route path="/purchase-report" element={<PurchaseReportPage />} />
        </Route>
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
